import {
  AuditOutlined,
  BugOutlined,
  BookOutlined,
  FileSyncOutlined,
  IdcardOutlined,
  ReadOutlined,
  CalendarOutlined,
  CommentOutlined,
  WalletOutlined,
  CreditCardOutlined,
  CustomerServiceOutlined,
  AppstoreOutlined,
  EditOutlined,
  FlagOutlined,
  QuestionCircleOutlined,
  HomeOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  ErrorComponent,
  RefineThemes,
  ThemedLayout,
  ThemedTitle,
  useNotificationProvider,
} from "@refinedev/antd";
import { Authenticated, CanAccess, Refine, useGetIdentity } from "@refinedev/core";
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
} from "@refinedev/react-router";
import { App as AntdApp, ConfigProvider, Grid, Result } from "antd";
import trTR from "antd/locale/tr_TR";
import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router";

import { AccountPage } from "./pages/account";
import { AuditLogPage } from "./pages/audit-log";
import { CancellationRequestsPage } from "./pages/cancellation-requests";
import { DashboardPage } from "./pages/dashboard";
import { InvitePage } from "./pages/invite";
import { LessonShowPage } from "./pages/lesson-show";
import { LessonsPage } from "./pages/lessons";
import { LoginPage, TwoFactorLoginPage } from "./pages/login";
import { ReviewsPage } from "./pages/reviews";
import { OrdersPage } from "./pages/orders";
import { PayoutsPage } from "./pages/payouts";
import { MenuBadge } from "./components/menu-badge";
import { MessageReportShowPage } from "./pages/message-report-show";
import { MessageReportsPage } from "./pages/message-reports";
import { ProfileChangeShowPage } from "./pages/profile-change-show";
import { ProfileChangesPage } from "./pages/profile-changes";
import { QuestionReportShowPage } from "./pages/question-report-show";
import { QuestionReportsPage } from "./pages/question-reports";
import { PlanEditPage } from "./pages/plan-edit";
import { PlanSeasonsPage } from "./pages/plan-seasons";
import { PlansPage } from "./pages/plans";
import { PricingPage } from "./pages/pricing";
import { BlogAuthorsPage } from "./pages/blog-authors";
import { BlogCategoriesPage } from "./pages/blog-categories";
import { BlogPostEditPage } from "./pages/blog-post-edit";
import { BlogPostsPage } from "./pages/blog-posts";
import { SupportPage } from "./pages/support";
import { SystemErrorShowPage } from "./pages/system-error-show";
import { SystemErrorsPage } from "./pages/system-errors";
import { SupportShowPage } from "./pages/support-show";
import { RolesPage } from "./pages/roles";
import { SetupTwoFactorPage } from "./pages/setup-two-factor";
import { StudentShowPage } from "./pages/student-show";
import { StudentsPage } from "./pages/students";
import { TeamPage } from "./pages/team";
import { TeamMemberPage } from "./pages/team-member";
import { TeamNewPage } from "./pages/team-new";
import { TutorShowPage } from "./pages/tutor-show";
import { TutorsPage } from "./pages/tutors";
import { UsersPage } from "./pages/users";
import { accessControlProvider } from "./providers/access-control-provider";
import { authProvider } from "./providers/auth-provider";
import { dataProvider } from "./providers/data-provider";
import { i18nProvider } from "./providers/i18n-provider";
import type { Me } from "./session";

// Office routes: docs/FRONTEND_ROUTES.md → Office Routes. The menu shows only resources the
// staff member's permissions open (providers/access-control-provider.ts).
const resources = [
  { name: "dashboard", list: "/", meta: { label: "Ana Sayfa", icon: <HomeOutlined /> } },
  {
    name: "tutors",
    list: "/tutors",
    show: "/tutors/:id",
    meta: { label: "Eğitmenler", icon: <SolutionOutlined /> },
  },
  {
    name: "profile-changes",
    list: "/profile-changes",
    show: "/profile-changes/:id",
    meta: {
      label: "Profil değişiklikleri",
      icon: (
        <MenuBadge count="profileChanges">
          <FileSyncOutlined />
        </MenuBadge>
      ),
    },
  },
  {
    name: "students",
    list: "/students",
    show: "/students/:id",
    meta: { label: "Öğrenciler", icon: <ReadOutlined /> },
  },
  { name: "users", list: "/users", meta: { label: "Kullanıcılar", icon: <IdcardOutlined /> } },
  {
    name: "lessons",
    list: "/lessons",
    show: "/lessons/:id",
    meta: { label: "Dersler", icon: <BookOutlined /> },
  },
  { name: "reviews", list: "/reviews", meta: { label: "Yorumlar", icon: <CommentOutlined /> } },
  {
    name: "cancellation-requests",
    list: "/cancellation-requests",
    meta: { label: "İptal Talepleri", icon: <CalendarOutlined /> },
  },
  {
    name: "orders",
    list: "/orders",
    meta: { label: "Öğrenci ödemeleri", icon: <CreditCardOutlined /> },
  },
  {
    name: "payouts",
    list: "/payouts",
    meta: { label: "Eğitmen ödemeleri", icon: <WalletOutlined /> },
  },
  {
    name: "plans",
    list: "/plans",
    create: "/plans/new",
    edit: "/plans/:id",
    meta: { label: "Planlar", icon: <AppstoreOutlined /> },
  },
  {
    name: "blog",
    list: "/blog",
    create: "/blog/new",
    edit: "/blog/:id",
    meta: { label: "Blog", icon: <EditOutlined /> },
  },
  {
    name: "support",
    list: "/support",
    show: "/support/:id",
    meta: {
      label: "Destek",
      icon: (
        <MenuBadge count="support">
          <CustomerServiceOutlined />
        </MenuBadge>
      ),
    },
  },
  {
    name: "message-reports",
    list: "/message-reports",
    show: "/message-reports/:id",
    meta: {
      label: "Bildirilen sohbetler",
      icon: (
        <MenuBadge count="messageReports">
          <FlagOutlined />
        </MenuBadge>
      ),
    },
  },
  {
    name: "question-reports",
    list: "/question-reports",
    show: "/question-reports/:id",
    meta: {
      label: "Bildirilen çözümler",
      icon: (
        <MenuBadge count="questionReports">
          <QuestionCircleOutlined />
        </MenuBadge>
      ),
    },
  },
  {
    name: "staff",
    list: "/team",
    create: "/team/new",
    show: "/team/:id",
    meta: { label: "Ekip", icon: <TeamOutlined /> },
  },
  {
    name: "roles",
    list: "/team/roles",
    meta: { label: "Roller", icon: <SafetyCertificateOutlined /> },
  },
  { name: "audit-log", list: "/audit-log", meta: { label: "Kayıtlar", icon: <AuditOutlined /> } },
  {
    name: "system-errors",
    list: "/system-errors",
    show: "/system-errors/:id",
    meta: { label: "Sistem hataları", icon: <BugOutlined /> },
  },
  { name: "account", list: "/account", meta: { label: "Hesabım", icon: <UserOutlined /> } },
];

/** Every panel screen needs the second factor; until it is set up only the setup page opens. */
function RequireTwoFactor({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useGetIdentity<Me>();
  if (isLoading) return null;
  if (me && !me.twoFactorEnabled) return <Navigate to="/setup-two-factor" replace />;
  return children;
}

/**
 * Below the `lg` breakpoint Refine turns the sider into a drawer whose toggle button is fixed
 * at the top left of the content; leave room for it so it does not cover page titles.
 */
function PanelContent({ children }: { children: ReactNode }) {
  const { lg } = Grid.useBreakpoint();
  return <div style={lg === false ? { paddingTop: 40 } : undefined}>{children}</div>;
}

function NoAccess() {
  return <Result status="403" title="Bu sayfaya erişim yetkin yok." />;
}

export function App() {
  return (
    <BrowserRouter>
      <ConfigProvider locale={trTR} theme={RefineThemes.Blue}>
        <AntdApp>
          <Refine
            dataProvider={dataProvider}
            i18nProvider={i18nProvider}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            routerProvider={routerProvider}
            notificationProvider={useNotificationProvider}
            resources={resources}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: false,
              disableTelemetry: true,
              // Labels are Turkish: no English plural/singular endings ("Eğitmenlers").
              textTransformers: { plural: (text) => text, singular: (text) => text },
              title: { text: "HedefUni Office" },
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated key="panel" fallback={<CatchAllNavigate to="/login" />}>
                    <RequireTwoFactor>
                      <ThemedLayout
                        Title={({ collapsed }) => (
                          <ThemedTitle collapsed={collapsed} text="HedefUni Office" />
                        )}
                      >
                        <PanelContent>
                          <CanAccess fallback={<NoAccess />}>
                            <Outlet />
                          </CanAccess>
                        </PanelContent>
                      </ThemedLayout>
                    </RequireTwoFactor>
                  </Authenticated>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="/tutors" element={<TutorsPage />} />
                <Route path="/tutors/:id" element={<TutorShowPage />} />
                <Route path="/profile-changes" element={<ProfileChangesPage />} />
                <Route path="/profile-changes/:id" element={<ProfileChangeShowPage />} />
                <Route path="/students" element={<StudentsPage />} />
                <Route path="/students/:id" element={<StudentShowPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/lessons" element={<LessonsPage />} />
                <Route path="/lessons/:id" element={<LessonShowPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/cancellation-requests" element={<CancellationRequestsPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/payouts" element={<PayoutsPage />} />
                <Route path="/plans" element={<PlansPage />} />
                <Route path="/plans/new" element={<PlanEditPage />} />
                <Route path="/plans/pricing" element={<PricingPage />} />
                <Route path="/plans/seasons" element={<PlanSeasonsPage />} />
                <Route path="/plans/:id" element={<PlanEditPage />} />
                <Route path="/blog" element={<BlogPostsPage />} />
                <Route path="/blog/new" element={<BlogPostEditPage />} />
                <Route path="/blog/authors" element={<BlogAuthorsPage />} />
                <Route path="/blog/categories" element={<BlogCategoriesPage />} />
                <Route path="/blog/:id" element={<BlogPostEditPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/support/:id" element={<SupportShowPage />} />
                <Route path="/message-reports" element={<MessageReportsPage />} />
                <Route path="/message-reports/:id" element={<MessageReportShowPage />} />
                <Route path="/question-reports" element={<QuestionReportsPage />} />
                <Route path="/question-reports/:id" element={<QuestionReportShowPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/team/new" element={<TeamNewPage />} />
                <Route path="/team/roles" element={<RolesPage />} />
                <Route path="/team/:id" element={<TeamMemberPage />} />
                <Route path="/audit-log" element={<AuditLogPage />} />
                <Route path="/system-errors" element={<SystemErrorsPage />} />
                <Route path="/system-errors/:id" element={<SystemErrorShowPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="*" element={<ErrorComponent />} />
              </Route>
              <Route
                element={
                  <Authenticated key="setup" fallback={<CatchAllNavigate to="/login" />}>
                    <Outlet />
                  </Authenticated>
                }
              >
                <Route path="/setup-two-factor" element={<SetupTwoFactorPage />} />
              </Route>
              <Route
                element={
                  <Authenticated key="auth" fallback={<Outlet />}>
                    <NavigateToResource resource="dashboard" />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<LoginPage />} />
                <Route path="/login/two-factor" element={<TwoFactorLoginPage />} />
              </Route>
              <Route path="/invite/:token" element={<InvitePage />} />
            </Routes>
            <DocumentTitleHandler handler={() => "HedefUni Office"} />
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}
